import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import type { ReactNode } from "react";
import { useDensity } from "../store/density";
import { ReadAloudButton } from "./ReadAloud";
import { TOAST_MS, ToastHost, useToasts } from "./Shell";

/**
 * 피코 말풍선 — 문구는 카탈로그 키에서만 온다(자유 텍스트 0 · DR-02). SE 에서는 읽어 주기 대상.
 * `busy` 는 로딩 애니메이션(prefers-reduced-motion 이면 정지).
 */
export interface PicoBubbleProps {
  text: string;
  avatar?: string;
  busy?: boolean;
  /** 축하 반응(올리기 완료 등 — SR-ACH-03). reduced-motion 이면 움직이지 않는다 */
  cheer?: boolean;
  /** 말풍선 변화를 스크린 리더에 알릴지(기본 true) */
  live?: boolean;
  aside?: ReactNode;
}

export function PicoBubble({
  text,
  avatar = "🤖",
  busy,
  cheer,
  live = true,
  aside,
}: PicoBubbleProps) {
  const { density } = useDensity();
  return (
    <div
      className={`pf-pico${busy ? " pf-pico--busy" : ""}${cheer ? " pf-pico--cheer" : ""}`}
      data-pico={cheer ? "cheer" : busy ? "busy" : undefined}
    >
      <div className="pf-pico__avatar" aria-hidden="true">
        {avatar}
      </div>
      <p
        className="pf-pico__bubble"
        role={live ? "status" : undefined}
        aria-live={live ? "polite" : undefined}
        data-read-aloud={density === "se" ? "" : undefined}
      >
        {text}
      </p>
      <ReadAloudButton text={text} />
      {aside}
    </div>
  );
}

// ── 토스트 변형 (FS-C1-07) ─────────────────────────────────────────────
// "3초 후 사라짐 · 같은 안내 반복 표시 안 함" (SCR v0.6). 자리·타이머는 Bench 이식 useToasts/ToastHost(./Shell)가
// 맡고, 여기서는 Field 규칙 하나를 더한다: 같은 key 는 **세션 동안** 한 번만(Bench 는 떠 있는 동안만 중복 제거).

export { TOAST_MS };

interface ToastCtx {
  /** key 가 같은 안내는 한 번만 보인다(세션 동안) */
  show: (key: string, text: string) => void;
}

const Ctx = createContext<ToastCtx | null>(null);

export function PicoToastProvider({
  children,
  durationMs = TOAST_MS,
}: {
  children: ReactNode;
  durationMs?: number;
}) {
  const { items, push, dismiss } = useToasts(durationMs);
  const seen = useRef(new Set<string>());

  const show = useCallback(
    (key: string, text: string) => {
      if (seen.current.has(key)) return;
      seen.current.add(key);
      push("info", text);
    },
    [push],
  );
  const value = useMemo(() => ({ show }), [show]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <ToastHost items={items} onDismiss={dismiss} />
    </Ctx.Provider>
  );
}

/** 토스트 한 장(단독 사용) — durationMs 뒤 onDone */
export function PicoToast({
  text,
  durationMs = TOAST_MS,
  onDone,
}: {
  text: string;
  durationMs?: number;
  onDone?: () => void;
}) {
  useEffect(() => {
    if (!onDone) return;
    const h = setTimeout(onDone, durationMs);
    return () => clearTimeout(h);
  }, [durationMs, onDone]);
  return (
    <div className="pf-toast" role="status" data-read-aloud="">
      <span aria-hidden="true">🤖</span> {text}
    </div>
  );
}

export function usePicoToast(): ToastCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("PicoToastProvider 밖에서 usePicoToast 를 불렀습니다");
  return v;
}
